import { Badge } from "@components/ui/Badge"
import { Button } from "@components/ui/Button"
import { cn } from "@lib/cn"
import { formatGrade } from "@lib/intl"
import { Camera, Check, RotateCcw } from "lucide-react"
import { memo, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import {
  gradeFor,
  isSettled,
  type WalkEntry,
  type WalkOutcome,
  type WalkSession,
} from "../scanSession"

import { useTaskProgress } from "@/realtime"
import type { GradeScale, StudentProgressRow } from "@/types"

interface Props {
  students: ReadonlyArray<StudentProgressRow>
  session: WalkSession
  taskId: string
  scale: GradeScale
  /** The student the camera is pointed at. */
  activeId: string | null
  onSelect: (studentId: string) => void
  /** A row's run reached a terminal state. */
  onSettled: (studentId: string, outcome: WalkOutcome, errorCode: string | null) => void
  busy: boolean
}

/**
 * The roster, walked (PRD-36 FR-2.1–2.3).
 *
 * The list is the surface: one row per student, the camera bound to whichever
 * row is active, and each row reporting its own outcome. Students who already
 * have a grade for this task are skipped by default and say so, with a re-scan
 * that asks through the same overwrite dialog as "Hoja por hoja" — FR-2.3 is
 * explicit that they are skipped, not hidden, because "did I already do
 * Ana?" is exactly the question a professor holding paper needs answered.
 */
export function ScanWalkList({
  students,
  session,
  taskId,
  scale,
  activeId,
  onSelect,
  onSettled,
  busy,
}: Props) {
  const { t } = useTranslation("ocr")

  return (
    // Rows between two hairlines rather than a card: the walk already sits in
    // the page's Card, and a card in a card is the nested-card pattern.
    <ul className="divide-hairline border-hairline divide-y border-y">
      {students.map((student) => {
        const entry = session[student.id]
        const existing = gradeFor(student, taskId)
        const isActive = student.id === activeId
        return (
          <li
            key={student.id}
            className={cn(
              "flex min-h-14 items-center gap-3 px-3 py-2",
              isActive && "bg-accent-soft",
            )}
          >
            {/* One live subscription per in-flight row. The hook is
                per-component by design, which is what makes five concurrent
                runs cost five subscriptions and no bookkeeping. */}
            {entry?.taskRunId ? (
              <RowProgress
                studentId={student.id}
                taskRunId={entry.taskRunId}
                onSettled={onSettled}
              />
            ) : null}

            <span className="min-w-0 flex-1">
              <span className={cn("text-ink block truncate text-sm", isActive && "font-semibold")}>
                {student.name}
              </span>
              <span className="text-meta block truncate">
                <RowCaption entry={entry} existing={existing} scale={scale} />
              </span>
            </span>

            <RowState entry={entry} existing={existing} />

            {/* The active row needs no button — the camera below is already
                pointed at it. Every other row offers the one verb that makes
                sense for its state. */}
            {!isActive ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => onSelect(student.id)}
                aria-label={
                  entry || existing !== null
                    ? t("walk.rescan_aria", { name: student.name })
                    : t("walk.scan_aria", { name: student.name })
                }
                leftIcon={
                  entry && isSettled(entry.state) ? (
                    <RotateCcw className="h-4 w-4" aria-hidden />
                  ) : (
                    <Camera className="h-4 w-4" aria-hidden />
                  )
                }
              />
            ) : (
              <Badge variant="accent">{t("walk.state.active")}</Badge>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function RowCaption({
  entry,
  existing,
  scale,
}: {
  entry: WalkEntry | undefined
  existing: number | null
  scale: GradeScale
}) {
  const { t } = useTranslation("ocr")
  if (entry?.state === "failed") {
    return <>{t("walk.caption.failed")}</>
  }
  if (entry?.state === "needs_review") return <>{t("walk.caption.needs_review")}</>
  if (entry?.state === "graded") return <>{t("walk.caption.graded")}</>
  if (entry?.state === "queued") return <>{t("walk.caption.queued")}</>
  if (existing !== null) {
    return <>{t("walk.caption.already", { grade: formatGrade(existing, scale) })}</>
  }
  return <>{t("walk.caption.waiting")}</>
}

function RowState({ entry, existing }: { entry: WalkEntry | undefined; existing: number | null }) {
  const { t } = useTranslation("ocr")
  if (!entry) {
    return existing !== null ? (
      <Badge variant="neutral">{t("walk.state.already")}</Badge>
    ) : (
      <span className="text-meta">{t("walk.state.pending")}</span>
    )
  }
  switch (entry.state) {
    case "queued":
      return <Badge variant="outline">{t("walk.state.queued")}</Badge>
    case "dispatching":
    case "processing":
      return <Badge variant="accent">{t("walk.state.processing")}</Badge>
    case "graded":
      return (
        <Badge variant="success">
          <Check className="h-3 w-3" aria-hidden />
          {t("walk.state.graded")}
        </Badge>
      )
    case "needs_review":
      return <Badge variant="attention">{t("walk.state.needs_review")}</Badge>
    case "failed":
      return <Badge variant="danger">{t("walk.state.failed")}</Badge>
    case "skipped":
      return <Badge variant="neutral">{t("walk.state.already")}</Badge>
  }
}

/**
 * Watches one TaskRun and reports the terminal state once.
 *
 * Renders nothing: the row's visible state comes from the session the parent
 * owns, so a re-render of this component can never disagree with its sibling.
 * `onSettled` rides a ref because the parent rebuilds the callback every time
 * the session changes — listing it in the deps would re-fire the effect on
 * every settlement in the whole walk.
 */
const RowProgress = memo(function RowProgress({
  studentId,
  taskRunId,
  onSettled,
}: {
  studentId: string
  taskRunId: string
  onSettled: (studentId: string, outcome: WalkOutcome, errorCode: string | null) => void
}) {
  const progress = useTaskProgress(taskRunId)
  const reportedRef = useRef(false)
  const settleRef = useRef(onSettled)
  useEffect(() => {
    settleRef.current = onSettled
  }, [onSettled])

  useEffect(() => {
    if (!progress || reportedRef.current) return
    const failed = progress.status === "failed"
    const completed = progress.status === "completed"
    if (!failed && !completed) return

    // The worker calls finish() on BOTH outcomes — auto-commit and deferred
    // review — so `completed` alone does not mean a grade was written.
    // `accept_path` is the discriminator and rides the same payload.
    const acceptPath = progress.metadata?.accept_path as string | undefined
    const code = progress.error_code ?? null
    reportedRef.current = true

    if (code === "upload_skipped_already_graded") {
      settleRef.current(studentId, "skipped", code)
      return
    }
    if (failed) {
      settleRef.current(studentId, "failed", code)
      return
    }
    settleRef.current(
      studentId,
      acceptPath === "manual_review_required" ? "needs_review" : "graded",
      null,
    )
  }, [progress, studentId])

  return null
})
